/**
 * Owns the lifecycle of the active Spatial Analysis run and its polling loop.
 *
 * Mounted above the map (like SimulationResultsProvider), so closing the
 * Spatial Analysis drawer does NOT stop the run — polling continues in the
 * background and the drawer re-reads state from here when reopened. The drawer
 * is purely presentational; this provider is the single owner of run state.
 */

import axios from 'axios';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import {
  cancelSpatialAnalysisRun,
  createSpatialAnalysisRun,
  getSpatialAnalysisRun,
  publishSpatialAnalysisRun,
} from '../services/spatialAnalysis/spatialAnalysisApi';
import {
  CreateSpatialAnalysisRunRequest,
  SpatialAnalysisRun,
  SpatialAnalysisRunStatus,
  isTerminalStatus,
} from '../services/spatialAnalysis/types';
import { trackAnalyticsEvent } from '../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../services/eventTaxonomy';

const POLL_INTERVAL_MS = 1200;
/** Give up after this many consecutive transient poll failures to avoid a zombie loop. */
const MAX_CONSECUTIVE_POLL_FAILURES = 5;

interface SpatialAnalysisContextValue {
  run: SpatialAnalysisRun | null;
  status: SpatialAnalysisRunStatus | 'idle';
  /** Step the run is currently working on (first running, else first pending) — drives the throbbing icon. */
  activeStepId: string | null;
  isStarting: boolean;
  error: string | null;
  startRun: (request: CreateSpatialAnalysisRunRequest) => Promise<SpatialAnalysisRun | null>;
  cancelRun: () => Promise<void>;
  clearRun: () => void;
  publish: () => Promise<{ available: boolean; message?: string } | null>;
}

const SpatialAnalysisContext = createContext<SpatialAnalysisContextValue | undefined>(undefined);

export function SpatialAnalysisProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [run, setRun] = useState<SpatialAnalysisRun | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const runIdRef = useRef<string | null>(null);
  const runStartTimeRef = useRef<number | null>(null);
  const prevStatusRef = useRef<SpatialAnalysisRunStatus | 'idle'>('idle');
  const pollFailureCountRef = useRef(0);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const poll = useCallback(async () => {
    const id = runIdRef.current;
    if (!id) {
      return;
    }
    try {
      const latest = await getSpatialAnalysisRun(id);
      // Ignore a stale poll that resolves after the run was cleared/replaced.
      if (runIdRef.current !== id) {
        return;
      }
      pollFailureCountRef.current = 0;
      setRun(latest);
      if (isTerminalStatus(latest.status)) {
        stopPolling();
      }
    } catch (err) {
      // Ignore a stale poll that rejects after the run was cleared/replaced.
      if (runIdRef.current !== id) {
        return;
      }
      // The backend no longer knows this run (wiped/expired). Stop and clear so we
      // don't hammer `GET /runs/{id}` with 404s forever (survives reloads otherwise).
      if (axios.isAxiosError(err) && err.response?.status === 404) {
        stopPolling();
        runIdRef.current = null;
        setRun(null);
        return;
      }
      // Transient/network error — keep polling; a later tick recovers. But give up
      // after a sustained run of failures so we don't spin forever.
      pollFailureCountRef.current += 1;
      if (pollFailureCountRef.current >= MAX_CONSECUTIVE_POLL_FAILURES) {
        stopPolling();
      }
    }
  }, [stopPolling]);

  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);
  }, [poll, stopPolling]);

  const startRun = useCallback(
    async (request: CreateSpatialAnalysisRunRequest): Promise<SpatialAnalysisRun | null> => {
      setIsStarting(true);
      setError(null);
      try {
        const created = await createSpatialAnalysisRun(request);
        runIdRef.current = created.id;
        runStartTimeRef.current = Date.now();
        pollFailureCountRef.current = 0;
        trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.spatial_analysis_run_started, {
          polygon_id: request.source.polygonId,
          vertex_count: request.polygonCoordinates[0]?.length ?? 0,
          has_source_layer: request.source.layerId != null,
          timestamp: new Date().toISOString(),
        });
        setRun(created);
        if (!isTerminalStatus(created.status)) {
          startPolling();
        }
        return created;
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to start spatial analysis');
        return null;
      } finally {
        setIsStarting(false);
      }
    },
    [startPolling],
  );

  const cancelRun = useCallback(async () => {
    const id = runIdRef.current;
    stopPolling();
    if (!id) {
      return;
    }
    try {
      const cancelled = await cancelSpatialAnalysisRun(id);
      if (runIdRef.current === id) {
        setRun(cancelled);
      }
    } catch {
      // Server cancel failed — still reflect Stop locally so the drawer shows the
      // cancelled state and the map's spatial layers get cleaned up.
      if (runIdRef.current === id) {
        setRun((prev) => (prev ? { ...prev, status: 'cancelled' } : prev));
      }
    }
  }, [stopPolling]);

  const clearRun = useCallback(() => {
    stopPolling();
    runIdRef.current = null;
    setRun(null);
    setError(null);
  }, [stopPolling]);

  const publish = useCallback(async () => {
    const id = runIdRef.current;
    if (!id) {
      return null;
    }
    return publishSpatialAnalysisRun(id);
  }, []);

  // Clear the interval if the provider unmounts (app teardown).
  useEffect(() => () => stopPolling(), [stopPolling]);

  // Emit a terminal analytics event once per status transition.
  useEffect(() => {
    const current: SpatialAnalysisRunStatus | 'idle' = run ? run.status : 'idle';
    if (current === prevStatusRef.current) {
      return;
    }
    prevStatusRef.current = current;
    if (!run) {
      return;
    }
    if (current === 'complete') {
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.spatial_analysis_run_completed, {
        run_id: run.id,
        layer_count: run.layers.length,
        drt_candidate_count: run.metrics.drt?.candidateCount,
        duration_ms: runStartTimeRef.current ? Date.now() - runStartTimeRef.current : undefined,
        // Census provenance for the resolved study area (e.g. 'ONS Census 2021',
        // "Scotland's Census 2022 (NRS)", 'ABS 2021'); undefined when off-coverage.
        census_source: run.metrics.population?.source,
        timestamp: new Date().toISOString(),
      });
      // UK BODS-powered runs: record that the Bus Open Data Service supplied the
      // transit layer (PRD-01). Counts/ids only.
      if (run.metrics.transit?.source === 'bods') {
        trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.bods_gather_used, {
          run_id: run.id,
          source: run.metrics.transit.source,
          stop_count: run.metrics.transit.stopCount,
          timestamp: new Date().toISOString(),
        });
      }
    } else if (current === 'failed') {
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.spatial_analysis_run_failed, {
        feature_area: 'spatial_analysis',
        error_category: run.error ? 'worker' : 'unknown',
        run_id: run.id,
        timestamp: new Date().toISOString(),
      });
    } else if (current === 'cancelled') {
      trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.spatial_analysis_run_cancelled, {
        run_id: run.id,
        timestamp: new Date().toISOString(),
      });
    }
  }, [run]);

  const activeStepId = useMemo(() => {
    if (!run) {
      return null;
    }
    const active =
      run.progress.find((step) => step.status === 'running') ??
      run.progress.find((step) => step.status === 'pending');
    return active ? active.id : null;
  }, [run]);

  const value = useMemo<SpatialAnalysisContextValue>(
    () => ({
      run,
      status: run ? run.status : 'idle',
      activeStepId,
      isStarting,
      error,
      startRun,
      cancelRun,
      clearRun,
      publish,
    }),
    [run, activeStepId, isStarting, error, startRun, cancelRun, clearRun, publish],
  );

  return <SpatialAnalysisContext.Provider value={value}>{children}</SpatialAnalysisContext.Provider>;
}

export function useSpatialAnalysis(): SpatialAnalysisContextValue {
  const ctx = useContext(SpatialAnalysisContext);
  if (!ctx) {
    throw new Error('useSpatialAnalysis must be used within a SpatialAnalysisProvider');
  }
  return ctx;
}
